import type { Dispatch, RefObject } from "react";
import type { Order, OrderPayment } from "../../lib/types.ts";
import type { PlanRateEingabe } from "../../lib/zahlungsplanEditor.ts";
import type { Aktion, ZahlungszielSnapshot } from "../../state/reduzierer.ts";
import { KonfliktFehler } from "../../api/basis.ts";
import { bestellungLaden, SSE_ENTITAET as BESTELLUNG_ENTITAET } from "../../api/bestellungen.ts";
import {
  planIndividualisieren,
  planSpeichern,
  zahlungszielSetzen,
  type ZahlungszielRaten,
} from "../../api/bestellungZahlungsziel.ts";
import { meldeFehler, meldeNachladeFehler, meldeNachladeSatz } from "../../state/fehlerStapel.ts";
import { BESTELLUNG_WEG_MELDUNG, mitBestellbezug } from "../../lib/bestellkennung.ts";
import { fehlertext } from "../../lib/fehlertext.ts";

/** Fingerabdruck "Raten-Ids und Raten-Versionen" fuer den Vergleich nach einem 409 (Reihenfolge egal). */
function ratenVersionen(raten: readonly OrderPayment[]): string {
  return JSON.stringify(raten.map((p) => [String(p.id ?? ""), Number(p.rowVersion)]).sort());
}

/**
 * 409 beim Speichern des Zahlungsplans (R4b): nichts wurde gespeichert. Erst wird nachgeladen, DANN steht GENAU EIN Eintrag
 * da, je nach Ausgang des Nachladens (R-T11f):
 *  - der Stand ist geladen: `PLAN_KONFLIKT_MELDUNG`;
 *  - das Laden scheiterte mit meldbarer Ursache: `PLAN_KONFLIKT_NACHLADEN_FEHLGESCHLAGEN_MELDUNG`, die Ursache dahinter;
 *  - das Laden endete ohne meldbaren Text (Abbruch, 401): `PLAN_KONFLIKT_NEUTRAL_MELDUNG`, nur was feststeht, ohne Aussage
 *    ueber den Stand (R-T10c);
 *  - das Laden lieferte `null` (die Bestellung gibt es nicht mehr): `BESTELLUNG_WEG_MELDUNG`.
 */
const PLAN_KONFLIKT_MELDUNG =
  "Der Zahlungsplan wurde zwischenzeitlich geändert und nicht gespeichert. Der aktuelle Stand ist geladen – bitte prüfen und erneut speichern.";
const PLAN_KONFLIKT_NEUTRAL_MELDUNG = "Der Zahlungsplan wurde zwischenzeitlich geändert und nicht gespeichert.";
const PLAN_KONFLIKT_NACHLADEN_FEHLGESCHLAGEN_MELDUNG = `${PLAN_KONFLIKT_NEUTRAL_MELDUNG} Bitte neu laden.`;

/** Ticket G: die Antwort sagt `ratenErsetzt: false` - gespeichert ist nur das Zahlungsziel, die Raten sind unverändert. */
const ZAHLUNGSZIEL_FESTGEHALTEN_MELDUNG = "Zahlungsziel festgehalten, Raten unverändert";

/** Der Teil der Antwort, den ein Vorgang an den Store gibt; `ratenErsetzt` steuert Snapshot und Hinweis. */
type PlanAntwort = ZahlungszielSnapshot & { ratenErsetzt?: boolean };

/**
 * Schreibt Drawer-Zahlungspläne mit der gemeinsamen Zahlungssperre und höchstens einem Versionsretry.
 * Jede FEHLERmeldung dieses Hooks geht seit der Fehleranzeige (Paket 1) in den Fehlerstapel; der einzige
 * Toast ist der Erfolgshinweis "Zahlungsziel festgehalten, Raten unverändert" (Ticket G).
 */
export function useDrawerZahlungsplan(
  bestellungen: readonly Order[],
  versende: Dispatch<Aktion>,
  zahlungsbearbeitungNachladenNoetigRef: RefObject<Set<string>>,
  zahlungsbearbeitungSperren: (id: string) => void,
  zahlungsbearbeitungFreigeben: (id: string) => void,
  istEingehaengt: RefObject<boolean>,
  drawerRef: RefObject<{ id: string } | null>,
  drawerSchliessen: () => void,
  NACHLADEN_FEHLGESCHLAGEN_MELDUNG: string,
  zeigeMeldung: (text: string) => void,
) {
  /**
   * `order_extras.row_version` der Bestellung aus dem Store - dieselbe Formel samt Rueckfall `0` wie
   * `drawerAendern` (siehe dortiger Kommentar): `0` trifft nie eine echte Version und endet in einem
   * lauten 404/409, nie in einem stillen Ueberschreiben.
   */
  function extrasVersionVon(id: string): number {
    const bestellung = bestellungen.find((b) => b.id === id);
    return Number((bestellung as { extrasRowVersion?: unknown } | undefined)?.extrasRowVersion) || 0;
  }

  /** Ein Vorgang am Zahlungsplan (siehe `drawerZahlungsplanSchreiben`). */
  interface ZahlungsplanVorgang {
    schreiben: () => Promise<PlanAntwort>;
    /** "nach <nachladenVorgang> nicht geladen". */
    nachladenVorgang: string;
    fehlerLog: string;
    fehlerMeldung: string;
    /** Bei 409 mit dem frisch geladenen Stand: die Wiederholung, oder `null` (dann Konflikt zeigen). */
    beiKonflikt?: (neu: Order) => (() => Promise<PlanAntwort>) | null;
  }

  /**
   * EIN Vorgang am Zahlungsplan einer Bestellung (Zahlungsziel setzen, Individuell anpassen, Plan
   * speichern): Schreibkanal sperren -> Endpunkt -> EIN Snapshot-Dispatch -> Gesamtstand nachladen ->
   * ein `nachgeladen`-Dispatch -> Kanal freigeben (Falle Store-Wettlauf, AGENTS.md: je Vorgang
   * genau eine Kette, nie zwei unabhaengige Dispatches derselben Bestellung).
   *
   * Die Antwort der Plan-Endpunkte hat `payments`, `paymentPlanSource`, `paymentTerms`,
   * `paymentTermsTemplateId` und `extrasRowVersion`; nur dieser Teil geht als Snapshot in den Store,
   * der Rest kommt mit dem Nachladen. Scheitert der Endpunkt (400/409), wird der Kanal wieder
   * freigegeben und die Meldung des Servers gezeigt; der Entwurf im Editor bleibt dabei stehen.
   */
  function drawerZahlungsplanSchreiben(id: string, vorgang: ZahlungsplanVorgang): void {
    if (zahlungsbearbeitungNachladenNoetigRef.current.has(id)) return;
    zahlungsbearbeitungSperren(id);
    zahlungsplanLauf(id, vorgang);
  }

  /**
   * R3 (Fehleranzeige Paket 1): ERFOLGE eines nicht mehr offenen Drawers duerfen verworfen werden, FEHLER nie.
   * Steht die Bestellung nicht (mehr) im Drawer, sieht der Nutzer nicht, welche gemeint ist; dann nennt der
   * Kontext sie. Beruehrt keinen Zustand, den ein anderer Drawer oder eine ausgehaengte App braucht.
   */
  function kontextFuer(id: string, satz: string): string {
    if (istEingehaengt.current && drawerRef.current?.id === id) return satz;
    const bestellung = bestellungen.find((b) => b.id === id);
    return mitBestellbezug(satz, bestellung, id);
  }

  /** Der Lauf eines Vorgangs bei BEREITS gehaltener Sperre (auch fuer die automatische Wiederholung nach 409). */
  function zahlungsplanLauf(id: string, vorgang: ZahlungsplanVorgang): void {
    void vorgang.schreiben().then(
      ({
        payments,
        paymentTerms,
        paymentTermsTemplateId,
        paymentPlanSource,
        extrasRowVersion,
        zahlungszielGewechseltAm,
        ratenErsetzt,
      }) => {
        if (!istEingehaengt.current) return;
        // Ticket G: `ratenErsetzt === false` heisst "nur das Zahlungsziel festgehalten". Die Raten der Antwort
        // sind roh und gleich den bestehenden: der Snapshot lässt `payments` dann WEG (der Reducer mischt per
        // Spread), sonst flackerte die bezahlte Rate bis zum Nachladen ohne ihre Ist-Zahlung.
        const nurFestgehalten = ratenErsetzt === false;
        // Der Merker "Zahlungsziel gewechselt" (nur die Antwort von PUT .../payment-terms traegt ihn) geht
        // ausdruecklich mit, auch als `null` (Zahlstatus-Reset); fehlt er in der Antwort, bleibt der Schluessel weg,
        // damit der Spread im Reducer den Wert im Store nicht mit `undefined` ueberschreibt.
        const merker = zahlungszielGewechseltAm === undefined ? {} : { zahlungszielGewechseltAm };
        versende({
          art: "zahlungszielSnapshot",
          id,
          snapshot: nurFestgehalten
            ? { paymentTerms, paymentTermsTemplateId, paymentPlanSource, extrasRowVersion, ...merker }
            : { payments, paymentTerms, paymentTermsTemplateId, paymentPlanSource, extrasRowVersion, ...merker },
        });
        if (nurFestgehalten) zeigeMeldung(ZAHLUNGSZIEL_FESTGEHALTEN_MELDUNG);
        return bestellungLaden(id).then(
          (neu) => {
            if (!istEingehaengt.current) return;
            if (neu !== null) {
              zahlungsbearbeitungFreigeben(id);
              versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
              return;
            }
            // Ohne bestaetigten Gesamtstand bleibt die vorhandene Bestellung sichtbar, aber ihre
            // Zahlungsdaten sind bis zum kompletten Reload gesperrt. Der Plan IST gespeichert (R4): die
            // Meldung sagt nur, dass die Anzeige neu geladen werden muss.
            if (drawerRef.current?.id === id) drawerSchliessen();
            meldeNachladeSatz(kontextFuer(id, NACHLADEN_FEHLGESCHLAGEN_MELDUNG));
          },
          (nachladeFehler: unknown) => {
            console.error(`Bestellung "${id}" konnte nach ${vorgang.nachladenVorgang} nicht geladen werden:`, nachladeFehler);
            // Die Antwort ist frisch, aber ohne bestaetigten Gesamtstand bleiben Zahlungs-Schreibwege
            // bis zum Seitenreload gesperrt. Gespeichert ist der Plan trotzdem (R4) - ein Fehler, der auch
            // nach einem Drawer-Wechsel oder Unmount nicht verloren gehen darf (R3).
            if (istEingehaengt.current && drawerRef.current?.id === id) drawerSchliessen();
            // R-Z2: kein Wiederholungsrat der Ursache hinter "gespeichert". Das Nachladen nach einem 409 (unten) bleibt
            // bei `meldeFehler`: dort wurde NICHTS gespeichert, erneutes Speichern ist der richtige Weg.
            meldeNachladeFehler(nachladeFehler, kontextFuer(id, NACHLADEN_FEHLGESCHLAGEN_MELDUNG));
          },
        );
      },
      (fehler: unknown) => {
        console.error(vorgang.fehlerLog, fehler);
        if (fehler instanceof KonfliktFehler && fehler.aktuellerStand !== undefined) {
          // Ausgehaengte App: nichts nachladen oder wiederholen, aber nicht verschweigen, dass der Plan NICHT
          // gespeichert wurde (R3).
          if (!istEingehaengt.current) {
            meldeFehler(kontextFuer(id, PLAN_KONFLIKT_NACHLADEN_FEHLGESCHLAGEN_MELDUNG));
            return;
          }
          // 409 MIT `current` = Versionskonflikt; ein 409 OHNE `current` ist fachlich (E6) und geht unten mit der Servermeldung in den Stapel, ohne Wiederholung. (veraltete Extras- oder Raten-Version, unbekannte Rate). Die Extras-Version springt auch
          // bei fachfremden Aenderungen (Board-Status, Notizen, DSV-Abgleich): sind Raten-Ids und alle
          // Raten-Versionen gegenueber dem Stand beim Senden unveraendert, geht der Vorgang EINMAL
          // automatisch mit der neuen Bestellversion erneut raus (Eingaben bleiben, kein Hinweis). Sonst
          // EIN Nachladen mit EINEM `nachgeladen`-Dispatch; der Editor behaelt seine Eingaben, wenn sich
          // am gespeicherten Planinhalt nichts geaendert hat, sonst setzt er zurueck und sagt es.
          void bestellungLaden(id).then(
            (neu) => {
              if (!istEingehaengt.current) {
                meldeFehler(kontextFuer(id, PLAN_KONFLIKT_NACHLADEN_FEHLGESCHLAGEN_MELDUNG));
                return;
              }
              const wiederholung = neu !== null ? (vorgang.beiKonflikt?.(neu) ?? null) : null;
              if (wiederholung !== null) {
                zahlungsplanLauf(id, { ...vorgang, schreiben: wiederholung, beiKonflikt: undefined });
                return;
              }
              if (neu !== null) {
                zahlungsbearbeitungFreigeben(id);
                versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
                // Der Plan wurde NICHT gespeichert: auch ein Nutzer, der inzwischen eine andere Bestellung
                // offen hat, muss das erfahren (R3).
                meldeFehler(kontextFuer(id, PLAN_KONFLIKT_MELDUNG));
                return;
              }
              // `null`: die Bestellung gibt es nicht mehr. Nichts geladen, nichts eingespielt - der Eintrag behauptet
              // keinen Stand und raet nicht zum erneuten Speichern (R-T11f).
              if (drawerRef.current?.id === id) drawerSchliessen();
              meldeFehler(kontextFuer(id, BESTELLUNG_WEG_MELDUNG));
            },
            (nachladeFehler: unknown) => {
              console.error(`Bestellung "${id}" konnte nach dem Versionskonflikt nicht geladen werden:`, nachladeFehler);
              if (istEingehaengt.current && drawerRef.current?.id === id) drawerSchliessen();
              // Dass der Plan NICHT gespeichert wurde, meldet R4b immer (R3: auch nach einem Drawer-Wechsel); nur der
              // Nachsatz "Bitte neu laden." und die Ursache haengen am Nachladen. Ohne meldbaren Text (Abbruch, 401;
              // `fehlertext` ergibt `null`) steht der Konfliktsatz allein - sonst bliebe der Eintrag ganz aus (R-T10c).
              const grund = fehlertext(nachladeFehler);
              if (grund === null) meldeFehler(kontextFuer(id, PLAN_KONFLIKT_NEUTRAL_MELDUNG));
              else meldeFehler(grund, kontextFuer(id, PLAN_KONFLIKT_NACHLADEN_FEHLGESCHLAGEN_MELDUNG));
            },
          );
          return;
        }
        if (istEingehaengt.current) zahlungsbearbeitungFreigeben(id);
        meldeFehler(fehler, kontextFuer(id, vorgang.fehlerMeldung));
      },
    );
  }

  /**
   * `raten` nur für "Zurück auf Vorlage" (`ersetzen`); die Vorlagenauswahl lässt es weg und bekommt den
   * Standard des Servers (`automatisch`: Raten erneuern oder nur festhalten, je nach Plan).
   */
  function drawerZahlungszielSetzen(id: string, templateId: string, raten?: ZahlungszielRaten): void {
    drawerZahlungsplanSchreiben(id, {
      schreiben: () =>
        raten === undefined ? zahlungszielSetzen(id, templateId) : zahlungszielSetzen(id, templateId, raten),
      nachladenVorgang: "dem Zahlungsziel",
      fehlerLog: `Zahlungsziel der Bestellung "${id}" konnte nicht gesetzt werden:`,
      fehlerMeldung: "Das Zahlungsziel konnte nicht gesetzt werden.",
    });
  }

  function drawerPlanIndividualisieren(id: string): void {
    // Wie beim Speichern: ein 409 durch eine fachfremde Extras-Aenderung (Raten-Ids und
    // -Versionen unveraendert) wird einmal mit der neuen Version wiederholt.
    const ratenStand = ratenVersionen(
      (bestellungen.find((b) => b.id === id)?.payments as OrderPayment[] | undefined) ?? [],
    );
    drawerZahlungsplanSchreiben(id, {
      schreiben: () => planIndividualisieren(id, extrasVersionVon(id)),
      beiKonflikt: (neu) =>
        ratenVersionen((neu.payments as OrderPayment[] | undefined) ?? []) === ratenStand
          ? () => planIndividualisieren(id, Number((neu as { extrasRowVersion?: unknown }).extrasRowVersion) || 0)
          : null,
      nachladenVorgang: "dem Individualisieren des Zahlungsplans",
      fehlerLog: `Zahlungsplan der Bestellung "${id}" konnte nicht individualisiert werden:`,
      fehlerMeldung: "Der Zahlungsplan konnte nicht individuell angepasst werden.",
    });
  }

  function drawerPlanSpeichern(id: string, raten: readonly PlanRateEingabe[]): void {
    const ratenStand = ratenVersionen(
      (bestellungen.find((b) => b.id === id)?.payments as OrderPayment[] | undefined) ?? [],
    );
    drawerZahlungsplanSchreiben(id, {
      schreiben: () => planSpeichern(id, extrasVersionVon(id), raten),
      beiKonflikt: (neu) =>
        ratenVersionen((neu.payments as OrderPayment[] | undefined) ?? []) === ratenStand
          ? () => planSpeichern(id, Number((neu as { extrasRowVersion?: unknown }).extrasRowVersion) || 0, raten)
          : null,
      nachladenVorgang: "dem Speichern des Zahlungsplans",
      fehlerLog: `Zahlungsplan der Bestellung "${id}" konnte nicht gespeichert werden:`,
      fehlerMeldung: "Der Zahlungsplan konnte nicht gespeichert werden.",
    });
  }

  return [drawerZahlungszielSetzen, drawerPlanIndividualisieren, drawerPlanSpeichern] as const;
}
